import {
  Button,
  Callout,
  Card,
  Input,
  LoadingRegion,
  Select,
  Skeleton,
  Switch,
} from '@apovisio/ui';
import { meetingCreateSchema, meetingUpdateSchema } from '@apovisio/contracts';
import type { MeetingDetail } from '@apovisio/contracts';
import { ArrowLeft, X } from 'lucide-react';
import { useRef, useState } from 'react';
import type { SyntheticEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { PageHeading } from '../../components/PageHeading';
import { useMe } from '../../features/auth/api';
import {
  useCreateMeeting,
  useMeeting,
  useUpdateMeeting,
  useUsers,
} from '../../features/meetings/api';
import { ApiClientError } from '../../lib/api';
import {
  fromLocalDateTimeInput,
  fromLocalInputs,
  toLocalDateInput,
  toLocalDateTimeInput,
  toLocalTimeInput,
} from '../../lib/format';
import { apiFieldErrors, zodFieldErrors } from '../../lib/form-errors';
import type { FieldErrors } from '../../lib/form-errors';

interface FormState {
  title: string;
  date: string;
  startTime: string;
  endTime: string;
  expiresAt: string;
  chatEnabled: boolean;
  screenShareEnabled: boolean;
  coHostIds: string[];
}

function defaultExpiry(): string {
  const date = new Date(Date.now() + 7 * 86_400_000);
  date.setHours(23, 59, 0, 0);
  return toLocalDateTimeInput(date.toISOString());
}

function initialState(meeting?: MeetingDetail): FormState {
  if (!meeting) {
    return {
      title: '',
      date: '',
      startTime: '',
      endTime: '',
      expiresAt: defaultExpiry(),
      chatEnabled: true,
      screenShareEnabled: false,
      coHostIds: [],
    };
  }
  return {
    title: meeting.title,
    date: toLocalDateInput(meeting.scheduledStart),
    startTime: toLocalTimeInput(meeting.scheduledStart),
    endTime: toLocalTimeInput(meeting.scheduledEnd),
    expiresAt: toLocalDateTimeInput(meeting.expiresAt),
    chatEnabled: meeting.chatEnabled,
    screenShareEnabled: meeting.screenShareEnabled,
    coHostIds: meeting.coHosts.map((c) => c.id),
  };
}

/** Écran 4 — Création / édition : options en fiche lisible. */
export function MeetingFormPage() {
  const { id } = useParams();
  const meeting = useMeeting(id ?? '');
  if (id && meeting.isPending) {
    return (
      <LoadingRegion label="Chargement de la visio">
        <Skeleton width="60%" height={28} />
      </LoadingRegion>
    );
  }
  if (id && !meeting.data) {
    return <Callout tone="danger" title="Visio introuvable" live="alert" />;
  }
  return <MeetingForm meeting={meeting.data} key={meeting.data?.id ?? 'new'} />;
}

function MeetingForm({ meeting }: { meeting: MeetingDetail | undefined }) {
  const navigate = useNavigate();
  const me = useMe();
  const users = useUsers();
  const create = useCreateMeeting();
  const update = useUpdateMeeting(meeting?.id ?? '');
  const [form, setForm] = useState<FormState>(() => initialState(meeting));
  const [errors, setErrors] = useState<FieldErrors>({});
  const summaryRef = useRef<HTMLDivElement>(null);
  const editing = Boolean(meeting);
  const pending = create.isPending || update.isPending;
  const set = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((current) => ({ ...current, [key]: value }));
  };

  const candidates = (users.data ?? []).filter(
    (user) => user.id !== me.data?.user.id && !form.coHostIds.includes(user.id),
  );

  function showErrors(fields: FieldErrors) {
    setErrors(fields);
    requestAnimationFrame(() => summaryRef.current?.focus());
  }

  function submit(event: SyntheticEvent<HTMLFormElement>) {
    event.preventDefault();
    const scheduledStart = fromLocalInputs(form.date, form.startTime);
    const scheduledEnd = fromLocalInputs(form.date, form.endTime);
    const base = {
      title: form.title,
      scheduledStart,
      scheduledEnd: scheduledStart ? scheduledEnd : null,
      expiresAt: fromLocalDateTimeInput(form.expiresAt) ?? '',
      chatEnabled: form.chatEnabled,
      screenShareEnabled: form.screenShareEnabled,
    };
    const fields: FieldErrors = {};
    if (form.date && !form.startTime) fields.scheduledStart = 'Indiquez l’heure de début.';
    const parsed = editing
      ? meetingUpdateSchema.safeParse(base)
      : meetingCreateSchema.safeParse({ ...base, coHostIds: form.coHostIds });
    if (!parsed.success) Object.assign(fields, zodFieldErrors(parsed.error));
    if (!base.expiresAt) fields.expiresAt = 'Choisissez une date d’expiration.';
    if (Object.keys(fields).length > 0) {
      showErrors(fields);
      return;
    }
    const onError = (error: unknown) => {
      showErrors(
        Object.keys(apiFieldErrors(error)).length > 0
          ? apiFieldErrors(error)
          : { _: error instanceof ApiClientError ? error.message : 'Enregistrement impossible.' },
      );
    };
    if (meeting) {
      update.mutate(base, {
        onSuccess: () => void navigate(`/admin/reunions/${meeting.id}`),
        onError,
      });
    } else {
      create.mutate(
        { ...base, coHostIds: form.coHostIds },
        { onSuccess: (detail) => void navigate(`/admin/reunions/${detail.id}`), onError },
      );
    }
  }

  const errorCount = Object.keys(errors).length;
  const backTo = meeting ? `/admin/reunions/${meeting.id}` : '/admin';

  return (
    <form className="app-form av-stack av-stack--lg" onSubmit={submit} noValidate>
      <div className="av-stack av-stack--sm">
        <Link to={backTo} className="app-back">
          <ArrowLeft className="av-icon" aria-hidden="true" />
          {meeting ? 'Retour à la visio' : 'Toutes les visios'}
        </Link>
        <PageHeading documentTitle={editing ? 'Modifier la visio' : 'Nouvelle visio'}>
          {editing ? 'Modifier la visio' : 'Nouvelle visio'}
        </PageHeading>
      </div>

      <div ref={summaryRef} tabIndex={-1} className="app-focus-target">
        {errorCount > 0 ? (
          <Callout
            tone="danger"
            title={errors._ ?? `${String(errorCount)} champ${errorCount > 1 ? 's' : ''} à corriger`}
            live="alert"
          />
        ) : null}
      </div>

      <Card title="L’essentiel">
        <Input
          label="Titre"
          value={form.title}
          maxLength={120}
          placeholder="Point mensuel"
          required
          onChange={(event) => {
            set('title', event.target.value);
          }}
          error={errors.title}
        />
        <div className="app-grid">
          <Input
            label="Date"
            type="date"
            value={form.date}
            hint="Créneau indicatif, sans invitation calendrier."
            onChange={(event) => {
              set('date', event.target.value);
            }}
          />
          <Input
            label="Début"
            type="time"
            value={form.startTime}
            onChange={(event) => {
              set('startTime', event.target.value);
            }}
            error={errors.scheduledStart}
          />
          <Input
            label="Fin"
            type="time"
            value={form.endTime}
            onChange={(event) => {
              set('endTime', event.target.value);
            }}
            error={errors.scheduledEnd}
          />
        </div>
      </Card>

      <Card title="Accès">
        <Input
          label="La visio expire le"
          type="datetime-local"
          value={form.expiresAt}
          required
          hint="Après cette date, plus aucun lien ne fonctionne et la salle est fermée."
          onChange={(event) => {
            set('expiresAt', event.target.value);
          }}
          error={errors.expiresAt}
        />
      </Card>

      <Card title="Options">
        <Switch
          label="Chat"
          description="Messages texte pendant la visio, jamais conservés."
          checked={form.chatEnabled}
          onChange={(value) => {
            set('chatEnabled', value);
          }}
        />
        <Switch
          label="Partage d’écran"
          description="Autorise les participants admis à partager leur écran."
          checked={form.screenShareEnabled}
          onChange={(value) => {
            set('screenShareEnabled', value);
          }}
        />
      </Card>

      {editing ? null : (
        <Card title="Co-hôtes" description="Ils peuvent admettre, refuser et révoquer.">
          {form.coHostIds.length > 0 ? (
            <ul className="app-list">
              {form.coHostIds.map((coHostId) => {
                const user = users.data?.find((u) => u.id === coHostId);
                return (
                  <li key={coHostId}>
                    <span>{user?.displayName ?? coHostId}</span>
                    <Button
                      variant="ghost"
                      icon={<X />}
                      aria-label={`Retirer ${user?.displayName ?? 'ce co-hôte'}`}
                      onClick={() => {
                        set(
                          'coHostIds',
                          form.coHostIds.filter((idToKeep) => idToKeep !== coHostId),
                        );
                      }}
                    >
                      Retirer
                    </Button>
                  </li>
                );
              })}
            </ul>
          ) : null}
          <Select
            label="Ajouter un co-hôte"
            value=""
            hint={
              candidates.length === 0
                ? 'Seuls les membres Apogée déjà connectés une fois apparaissent ici.'
                : undefined
            }
            disabled={candidates.length === 0}
            onChange={(event) => {
              if (event.target.value) set('coHostIds', [...form.coHostIds, event.target.value]);
            }}
            error={errors.coHostIds}
          >
            <option value="">Choisir un membre Apogée</option>
            {candidates.map((user) => (
              <option key={user.id} value={user.id}>
                {user.displayName} ({user.email})
              </option>
            ))}
          </Select>
        </Card>
      )}

      <div className="av-row app-form__actions">
        <Button
          variant="secondary"
          onClick={() => {
            void navigate(backTo);
          }}
        >
          Annuler
        </Button>
        <Button type="submit" loading={pending}>
          {editing ? 'Enregistrer' : 'Créer la visio'}
        </Button>
      </div>
    </form>
  );
}
